import React, { useState } from "react";
import {
  useGetUserListQuery,
  useDeleteUserMutation,
} from "../store/api/userApi";
import { Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import Datatable from "../components/Datatable";
import UserFormModal from "../components/modals/UserFormModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import SearchInput from "../components/SearchInput";

const UserManagement = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const limit = 10;

  const { data, isLoading, error } = useGetUserListQuery({
    limit,
    offset: (page - 1) * limit,
    search,
  });

  const [deleteUser] = useDeleteUserMutation();

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleEdit = (id) => {
    setSelectedId(id);
    setFormModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedId(null); // null → create mode
    setFormModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    await deleteUser(deleteId);
    setConfirmOpen(false);
  };

  const columns = [
    {
      id: "name",
      label: "Name",
      headClass: "min-w-[150px]",
      render: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      id: "email",
      label: "Email",
      headClass: "min-w-[180px]",
    },
    {
      id: "role",
      label: "Role",
      headClass: "min-w-[120px]",
    },
    {
      id: "actions",
      label: "Actions",
      headClass: "w-[120px]",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={() => handleEdit(row.id)}
          >
            <Pencil className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 text-red-500 hover:text-red-500"
            onClick={() => handleDeleteClick(row.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-md font-bold">Users List</h1>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create User
            </Button>
          </div>

          <div className="">
            <SearchInput search={search} handleSearch={handleSearch} />
          </div>
        </div>

        {/* DATATABLE */}
        <Datatable
          limit={limit}
          page={page}
          setPage={setPage}
          columns={columns}
          rows={data?.data || []}
          isLoading={isLoading}
          error={error}
          total={data?.pagination?.total || 0}
        />
      </div>

      {/* Add/Edit User Modal */}
      <UserFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        id={selectedId}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        description="Are you sure you want to delete this user?"
      />
    </>
  );
};

export default UserManagement;
